import { fontText, hexToRgbA } from 'common-react';
import { AxisSetExtremesEventObject, Options, SeriesOptionsType, YAxisOptions } from 'highcharts/highstock';
import { Theme } from '../styles/styles';

export type StockSeriesEntry = {
  name: string;
  color: string;
};

export type LegendLabelVariant = 'compact' | 'pill';

type LegendLabelStyle = {
  font: string;
  padding: string;
  borderRadius: string;
  dotSize: number;
  margin: string;
  dotMarginRight: string;
};

const compactFlatLegendStyle: Partial<LegendLabelStyle> = {
  padding: '3px 12px',
  margin: 'margin: 0;',
  dotSize: 8,
  dotMarginRight: '8px',
};

type BuildSharedLegendLabelFormatterParams = {
  theme: Theme;
  variant?: LegendLabelVariant;
  styleOverride?: Partial<LegendLabelStyle>;
};

type BuildSharedStockSeriesParams<TDataPoint> = {
  datas: TDataPoint[][];
  entries: StockSeriesEntry[];
  hiddenSeriesNames?: string[];
  withDailyDataGrouping?: boolean;
};

type BuildSharedStockChartBaseOptionsParams = {
  theme: Theme;
  isDarkTheme?: boolean;
  series: SeriesOptionsType[];
  handleNavigatorChange: (event: AxisSetExtremesEventObject) => void;
  handleLegendVisibilityChange?: (seriesName: string) => void;
  tooltip: Record<string, unknown>;
  legendLabelFormatter: (this: LegendFormatterContext) => string;
  legendEnabled?: boolean;
  chartHeight?: number | null;
  isNavigatorEnabled?: boolean;
  axisLightColor?: string;
  axisLabelColor?: string;
  navigatorGridLineLightColor?: string;
  navigatorLabelColor?: string;
  xAxisMin?: number;
  xAxisMax?: number;
  timeUseUTC?: boolean;
  disableMouseWheelZoom?: boolean;
  legendOverrides?: Partial<NonNullable<Options['legend']>>;
  yAxisOverrides?: Record<string, unknown>;
};

type BuildCompactLegendOptionsParams = {
  theme: Theme;
  isMobile: boolean;
};

type LegendFormatterContext = {
  color?: string;
  name?: string;
};

const legendVariantStyles = {
  compact: {
    padding: '4px 8px',
    borderRadius: '4px',
    dotSize: 6,
    margin: '',
    dotMarginRight: '4px',
  },
  pill: {
    padding: '6px 16px',
    borderRadius: '20px',
    dotSize: 8,
    margin: 'margin: 3px 0;',
    dotMarginRight: '4px',
  },
} as const;

export const buildSharedLegendLabelFormatter = ({
  theme,
  variant = 'pill',
  styleOverride,
}: BuildSharedLegendLabelFormatterParams) => {
  const currentVariant = {
    font: fontText('normal', theme.fontSize.base, 24),
    ...legendVariantStyles[variant],
    ...styleOverride,
  };

  return function(this: LegendFormatterContext) {
    const dotColor = this?.color ?? 'transparent';
    const label = this?.name ?? '';

    return `
      <div class="fund-legend-item" style="
          display: flex;
          align-items: center;
          font: ${currentVariant.font};
          padding: ${currentVariant.padding};
          border: 1px solid ${dotColor};
          border-radius: ${currentVariant.borderRadius};
          color: ${theme.color.greys[80]};
          ${currentVariant.margin}
        "
      >
        <div class="fund-legend-dot" style="
            width: ${currentVariant.dotSize}px;
            height: ${currentVariant.dotSize}px;
            border-radius: 50%;
            background-color: ${dotColor};
            margin-right: ${currentVariant.dotMarginRight};
            flex-shrink: 0;
          "
        ></div>
        <span style="
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            position: relative;
            top: 1px;
          "
        >${label}</span>
      </div>
    `;
  };
};

export const buildCompactLegendOptions = ({
  theme,
  isMobile,
}: BuildCompactLegendOptionsParams): Pick<BuildSharedStockChartBaseOptionsParams, 'legendLabelFormatter' | 'legendOverrides'> => ({
  legendLabelFormatter: buildSharedLegendLabelFormatter({
    theme,
    variant: 'compact',
    styleOverride: isMobile ? {
      ...compactFlatLegendStyle,
      font: fontText('normal', theme.fontSize.s, 20),
      padding: '2px 8px',
      dotSize: 6,
      dotMarginRight: '4px',
    } : compactFlatLegendStyle,
  }),
  legendOverrides: {
    itemMarginBottom: 2,
    alignColumns: false,
    ...(isMobile ? {
      margin: 4,
      y: 0,
      itemDistance: 4,
    } : {}),
  },
});

export const buildSharedStockSeries = <TDataPoint>({
  datas,
  entries,
  hiddenSeriesNames = [],
  withDailyDataGrouping = false,
}: BuildSharedStockSeriesParams<TDataPoint>): SeriesOptionsType[] =>
    entries.map((entry, i) => ({
      type: 'spline',
      name: entry.name,
      data: datas[i] || [],
      visible: !hiddenSeriesNames.includes(entry.name),
      marker: { enabled: false },
      color: entry.color,
      colorIndex: i,
      boostThreshold: 1000,
      turboThreshold: 0,
      animation: !(/Android/i.test(navigator.userAgent)),
      ...(withDailyDataGrouping ? {
        dataGrouping: {
          forced: true,
          units: [
            ['day', [1]],
          ],
        },
      } : {}),
    } as SeriesOptionsType));

export const buildSharedStockChartBaseOptions = ({
  theme,
  isDarkTheme = false,
  series,
  handleNavigatorChange,
  handleLegendVisibilityChange,
  tooltip,
  legendLabelFormatter,
  legendEnabled = true,
  chartHeight,
  isNavigatorEnabled = true,
  axisLightColor = theme.color.greys[10],
  axisLabelColor = theme.color.greys[40],
  navigatorGridLineLightColor = theme.color.greys[10],
  navigatorLabelColor = theme.color.greys[40],
  xAxisMin,
  xAxisMax,
  timeUseUTC,
  disableMouseWheelZoom = false,
  legendOverrides,
  yAxisOverrides,
}: BuildSharedStockChartBaseOptionsParams): Options => {
  const { labels: yAxisLabelsOverride, ...yAxisOverridesRest } = yAxisOverrides ?? {};

  return {
    accessibility: {
      keyboardNavigation: {
        order: ['legend', 'rangeSelector', 'series', 'zoom', 'navigator', 'chartMenu', 'container'],
        focusBorder: {
          style: {
            color: theme.color.oranges[50],
            borderRadius: 20,
          },
        },
      },
    },
    ...(timeUseUTC !== undefined ? {
      time: {
        useUTC: timeUseUTC,
      } as Options['time'],
    } : {}),
    exporting: {
      enabled: false,
    },
    chart: {
      backgroundColor: theme.color.greys[0],
      marginLeft: 2,
      marginRight: 2,
      spacingLeft: 0,
      spacingRight: 0,
      height: chartHeight,
      ...(disableMouseWheelZoom ? {
        zooming: {
          mouseWheel: {
            enabled: false,
          },
        },
      } : {}),
      style: {
        fontFamily: theme.fontFamily.ingMeWebBold,
      },
    },
    series,
    plotOptions: {
      series: {
        events: {
          legendItemClick: function(this: { name?: string }) {
            if (this.name) {
              handleLegendVisibilityChange?.(this.name);
            }
          },
        },
      },
    },
    legend: {
      enabled: legendEnabled,
      margin: 30,
      itemMarginBottom: 8,
      symbolPadding: 0,
      symbolWidth: 0,
      symbolHeight: 0,
      squareSymbol: false,
      align: 'left',
      verticalAlign: 'top',
      layout: 'horizontal',
      x: -5,
      y: 0,
      useHTML: true,
      labelFormatter: legendLabelFormatter as NonNullable<NonNullable<Options['legend']>['labelFormatter']>,
      itemDistance: 8,
      itemHiddenStyle: {
        color: theme.color.greys[50],
        textDecoration: 'none',
      },
      itemHoverStyle: undefined,
      ...legendOverrides,
    },
    title: { text: '' },
    yAxis: {
      gridLineColor: isDarkTheme ? hexToRgbA(theme.color.greys[80], 0.05) : axisLightColor,
      gridLineWidth: 1,
      // `enabled` is a deprecated but still-functional Highcharts title option, missing from the shipped typings.
      title: { enabled: false } as YAxisOptions['title'],
      labels: {
        style: {
          color: axisLabelColor,
          whiteSpace: 'nowrap',
          font: fontText('normal', theme.fontSize.s, 16),
        },
        ...(typeof yAxisLabelsOverride === 'object' && yAxisLabelsOverride != null
          ? yAxisLabelsOverride
          : {}),
      },
      ...yAxisOverridesRest,
    },
    xAxis: {
      min: xAxisMin,
      max: xAxisMax,
      lineColor: isDarkTheme ? hexToRgbA(theme.color.greys[80], 0.1) : axisLightColor,
      gridLineColor: isDarkTheme ? hexToRgbA(theme.color.greys[80], 0.05) : axisLightColor,
      gridLineWidth: 1,
      tickLength: 0,
      crosshair: {
        color: axisLabelColor,
        dashStyle: 'Dash',
      },
      events: {
        setExtremes: handleNavigatorChange,
      },
      labels: {
        style: {
          color: axisLabelColor,
          font: fontText('normal', theme.fontSize.s, 16),
        },
      },
    },
    navigator: {
      enabled: isNavigatorEnabled,
      handles: {
        lineWidth: 0,
        width: 4,
        backgroundColor: hexToRgbA(theme.color.violets[100], 0.6),
        borderColor: theme.color.violets[100],
        height: 48,
      },
      height: 40,
      maskFill: hexToRgbA(theme.color.violets[100], 0.2),
      outlineWidth: 0,
      series: {
        color: undefined,
        fillOpacity: 0,
        lineColor: theme.color.violets[100],
      },
      xAxis: {
        gridLineWidth: 1,
        gridLineColor: isDarkTheme ? hexToRgbA(theme.color.greys[80], 0.3) : navigatorGridLineLightColor,
        labels: {
          style: {
            textOutline: 'none',
            color: navigatorLabelColor,
            opacity: 1,
          },
        },
      },
    },
    credits: { enabled: false },
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    tooltip: tooltip as Options['tooltip'],
  };
};
